// Style imports
import './styles/variables.css';
import './styles/base.css';
import './styles/layout.css';
import './styles/components.css';

document.addEventListener('DOMContentLoaded', () => {
  // 1. Page Loader / FOUC Prevention
  requestAnimationFrame(() => {
    document.body.classList.add('loaded');
  });

  // 2. Scroll Spy & Navigation Highlight
  initScrollSpy();

  // 3. Testimonials Carousel
  initCarousel();

  // 4. Gallery Lightbox
  initLightbox();

  // 5. Square Booking Form Integration
  initBookingForm();

  // 6. Night Mode Theme Switcher
  initThemeToggle();
});

/* ==========================================================================
   2. Scroll Spy Logic
   ========================================================================== */
function initScrollSpy() {
  const sections = document.querySelectorAll('section[id], header[id]');
  const navLinks = document.querySelectorAll('.nav-links a');

  const observerOptions = {
    root: null,
    rootMargin: '-30% 0px -50% 0px', // Triggers when section occupies middle/top viewport
    threshold: 0
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const id = entry.target.getAttribute('id');
        navLinks.forEach(link => {
          link.classList.remove('active');
          if (link.getAttribute('href') === `#${id}`) {
            link.classList.add('active');
          }
        });
      }
    });
  }, observerOptions);

  sections.forEach(section => observer.observe(section));
}

/* ==========================================================================
   3. Testimonials Carousel Logic
   ========================================================================== */
function initCarousel() {
  const slides = document.querySelectorAll('.carousel-slide');
  const dots = document.querySelectorAll('.dot-btn');
  const prevBtn = document.querySelector('.prev-btn');
  const nextBtn = document.querySelector('.next-btn');
  const carouselContainer = document.querySelector('.testimonials-carousel');

  if (!slides.length || !dots.length) return;

  let currentIndex = 0;
  let autoRotateInterval: number | undefined;

  function showSlide(index: number) {
    // Wrap around logic
    if (index >= slides.length) currentIndex = 0;
    else if (index < 0) currentIndex = slides.length - 1;
    else currentIndex = index;

    // Toggle active slide class
    slides.forEach((slide, i) => {
      slide.classList.remove('active');
      if (i === currentIndex) {
        slide.classList.add('active');
      }
    });

    // Toggle active dot class
    dots.forEach((dot, i) => {
      dot.classList.remove('active');
      if (i === currentIndex) {
        dot.classList.add('active');
      }
    });
  }

  function startAutoRotate() {
    autoRotateInterval = window.setInterval(() => {
      showSlide(currentIndex + 1);
    }, 6000); // Rotate every 6 seconds
  }

  function stopAutoRotate() {
    if (autoRotateInterval) {
      clearInterval(autoRotateInterval);
      autoRotateInterval = undefined;
    }
  }

  // Next / Prev clicks
  prevBtn?.addEventListener('click', () => {
    stopAutoRotate();
    showSlide(currentIndex - 1);
    startAutoRotate();
  });

  nextBtn?.addEventListener('click', () => {
    stopAutoRotate();
    showSlide(currentIndex + 1);
    startAutoRotate();
  });

  // Dots clicks
  dots.forEach((dot, idx) => {
    dot.addEventListener('click', () => {
      stopAutoRotate();
      showSlide(idx);
      startAutoRotate();
    });
  });

  // Pause on hover
  carouselContainer?.addEventListener('mouseenter', stopAutoRotate);
  carouselContainer?.addEventListener('mouseleave', startAutoRotate);

  // Initialize
  showSlide(0);
  startAutoRotate();
}

/* ==========================================================================
   4. Gallery Lightbox Logic
   ========================================================================== */
function initLightbox() {
  const galleryItems = document.querySelectorAll('.gallery-item');
  if (!galleryItems.length) return;

  // Create Lightbox Markup dynamically
  const lightbox = document.createElement('div');
  lightbox.className = 'lightbox';
  lightbox.setAttribute('role', 'dialog');
  lightbox.setAttribute('aria-modal', 'true');
  lightbox.innerHTML = `
    <button class="lightbox-close" aria-label="Close lightbox">&times;</button>
    <img class="lightbox-content" src="" alt="Enlarged gallery view">
    <div class="lightbox-caption"></div>
  `;
  document.body.appendChild(lightbox);

  const lightboxImg = lightbox.querySelector('.lightbox-content') as HTMLImageElement;
  const lightboxCaption = lightbox.querySelector('.lightbox-caption') as HTMLDivElement;
  const closeBtn = lightbox.querySelector('.lightbox-close') as HTMLButtonElement;

  function openLightbox(imgSrc: string, altText: string, name: string, breed: string) {
    if (!lightboxImg || !lightboxCaption) return;
    lightboxImg.src = imgSrc;
    lightboxImg.alt = altText;
    lightboxCaption.innerHTML = `<strong>${name}</strong> &ndash; ${breed}`;
    lightbox.classList.add('active');
    document.body.style.overflow = 'hidden'; // Lock page scroll
  }

  function closeLightbox() {
    lightbox.classList.remove('active');
    document.body.style.overflow = ''; // Release page scroll
  }

  // Attach click events to gallery items
  galleryItems.forEach(item => {
    item.addEventListener('click', () => {
      const img = item.querySelector('img');
      const petNameEl = item.querySelector('.pet-name');
      const petBreedEl = item.querySelector('.pet-breed');

      if (img) {
        const imgSrc = img.getAttribute('src') || '';
        const altText = img.getAttribute('alt') || '';
        const petName = petNameEl?.textContent || 'Happy Client';
        const petBreed = petBreedEl?.textContent || 'Pet Care';
        openLightbox(imgSrc, altText, petName, petBreed);
      }
    });
  });

  // Close Events
  closeBtn.addEventListener('click', closeLightbox);
  lightbox.addEventListener('click', (e) => {
    if (e.target === lightbox) {
      closeLightbox();
    }
  });

  // Escape key handler
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && lightbox.classList.contains('active')) {
      closeLightbox();
    }
  });
}

/* ==========================================================================
   5. Square Booking Form Redirect Simulation
   ========================================================================== */
function initBookingForm() {
  const form = document.getElementById('booking-form') as HTMLFormElement;
  if (!form) return;

  // Create Toast element dynamically
  const toast = document.createElement('div');
  toast.className = 'toast-notification';
  toast.innerHTML = `
    <span class="toast-title"></span>
    <span class="toast-message"></span>
  `;
  document.body.appendChild(toast);

  const toastTitle = toast.querySelector('.toast-title') as HTMLSpanElement;
  const toastMsg = toast.querySelector('.toast-message') as HTMLSpanElement;

  function showToast(title: string, message: string) {
    if (!toastTitle || !toastMsg) return;
    toastTitle.textContent = title;
    toastMsg.textContent = message;
    toast.classList.add('active');

    // Auto-remove after 4 seconds
    setTimeout(() => {
      toast.classList.remove('active');
    }, 4000);
  }

  form.addEventListener('submit', (e) => {
    e.preventDefault();

    const parentNameField = document.getElementById('pet-parent-name') as HTMLInputElement;
    const petNameField = document.getElementById('pet-name-field') as HTMLInputElement;
    const serviceField = document.getElementById('booking-service') as HTMLSelectElement;

    if (!parentNameField || !petNameField || !serviceField) return;

    const parentName = parentNameField.value;
    const petName = petNameField.value;
    const serviceOption = serviceField.options[serviceField.selectedIndex].text;

    // Toast Feedback
    showToast(
      '🐾 Consultation Request Sent!',
      `Thank you ${parentName}! We are redirecting you to Mari's Square Appointments schedule to complete your slot booking for ${petName} (${serviceOption}).`
    );

    // Redirect simulation after 2.5 seconds (premium high-converting pipeline feel)
    setTimeout(() => {
      // Mock Square Appointments URL - direct link
      window.open('https://squareup.com/appointments/book/marinahound-by-mari-mock', '_blank');
      form.reset();
    }, 2500);
  });
}

/* ==========================================================================
   6. Theme Toggle Logic
   ========================================================================== */
function initThemeToggle() {
  const toggleBtn = document.getElementById('theme-toggle');
  if (!toggleBtn) return;

  // Read saved selection or fallback to OS preference
  const savedTheme = localStorage.getItem('theme');
  const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  const currentTheme = savedTheme || (systemPrefersDark ? 'dark' : 'light');

  // Set initial attribute
  document.documentElement.setAttribute('data-theme', currentTheme);

  // Toggle onClick
  toggleBtn.addEventListener('click', () => {
    const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
    const newTheme = isDark ? 'light' : 'dark';
    
    document.documentElement.setAttribute('data-theme', newTheme);
    localStorage.setItem('theme', newTheme);
  });
}

console.log('🐾 MarinaHound by Mari initialized!');
